// Sprint 6-organs §6.2 — контейнер разреза «По органам» (host: MarkersTab).
// Data-driven список тайлов (Q7: только органы с данными). 0 анализов → empty-state
// с CTA-паттерном Главной «Загрузите первый анализ» (переиспользуем UploadSourcePicker).

import { useState, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { UploadCloud } from 'lucide-react';
import { useOrgans } from '@/api/hooks/organs';
import { IconTile } from '@/components/ui/icon-tile';
import { OrganTile } from '@/components/dashboard/OrganTile';
import { FullScreenSpinner } from '@/components/FullScreenSpinner';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { UploadSourcePicker } from '@/components/uploads/UploadSourcePicker';

export function OrganGroupedView({
  onOpenOrgan,
  analysesCount,
  footer,
}: {
  onOpenOrgan: (code: string) => void;
  /**
   * dashboard.analyses_count — различает «0 анализов» и «анализы есть, но нет органных
   * показателей». `null` = ещё грузится (unknown): empty-state не выбираем до загрузки,
   * иначе кратко мигнул бы CTA «Загрузите первый анализ» (R1 Low).
   */
  analysesCount: number | null;
  /**
   * HEAL-18: хвост списка — карточка «Дополнительные показатели» (маркеры вне
   * справочника). Считается из dashboard.extras_count, т.е. НЕ зависит от того,
   * загрузился ли список органов, — поэтому рендерится и в ветке ошибки (падение
   * GET /organs не должно прятать вход в extras, иначе счётчик разъедется с
   * «По категориям», а AC HEAL-18 требует одинаковых точек входа). Не рендерится
   * только под спиннером (нечего показывать, пока не решили, что за экран) и в CTA
   * «загрузите первый анализ» (там analyses_count = 0, а значит и extras_count = 0).
   */
  footer?: ReactNode;
}) {
  const { data, isPending, isError } = useOrgans();
  const [, setLocation] = useLocation();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  if (isPending) return <FullScreenSpinner />;
  if (isError) {
    return (
      <div className="px-4">
        <div className="p-6 text-center text-sm text-muted-foreground">
          Не удалось загрузить органы.
        </div>
        {footer}
      </div>
    );
  }

  const organs = data?.organs ?? [];

  if (organs.length === 0) {
    // dashboard ещё грузится — не знаем, какой из empty-state показать; ждём (R1 Low).
    if (analysesCount === null) return <FullScreenSpinner />;
    // Анализы есть, но ни один показатель не привязан к органу (R1 Med: не звать
    // «загрузите первый», когда загрузки уже есть) → мягкая подсказка + вид «По категориям».
    if (analysesCount > 0) {
      return (
        <div className="px-4 pt-2">
          <div className="rounded-surface border border-border bg-surface p-5 text-center">
            <p className="text-sm font-medium text-foreground">
              Нет показателей, связанных с органами
            </p>
            <p className="mt-1 text-hl-secondary text-muted-foreground">
              В загруженных анализах пока нет таких показателей. Полный список — во вкладке «По&nbsp;категориям».
            </p>
          </div>
          {footer && <div className="pt-2">{footer}</div>}
        </div>
      );
    }

    // Q7: 0 анализов → пустой экран + CTA «Загрузите первый анализ».
    return (
      <div className="px-4 pt-2">
        <section className="rounded-surface bg-primary p-5 text-primary-foreground">
          <div className="mb-4 flex items-start gap-3">
            <IconTile size="md" tone="inverse">
              <UploadCloud className="h-6 w-6" aria-hidden="true" />
            </IconTile>
            <div>
              <div className="mb-1 text-lg font-semibold leading-tight">
                Загрузите первый анализ
              </div>
              <div className="text-sm leading-snug text-white/90">
                Соберём показатели по органам — что в норме, что требует внимания
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsSheetOpen(true)}
            className="block w-full rounded-surface bg-surface py-3 text-sm font-semibold text-primary-text"
          >
            Загрузить анализ
          </button>
        </section>

        <BottomSheet
          open={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          ariaLabel="Выбор источника файла"
        >
          <UploadSourcePicker
            onClose={() => setIsSheetOpen(false)}
            onUploaded={(uploadId) => setLocation(`/upload/processing/${uploadId}`)}
            onBatchCreated={(batchId) => setLocation(`/uploads/batch/${batchId}`)}
          />
        </BottomSheet>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 px-4">
      {organs.map((organ) => (
        <OrganTile
          key={organ.organ_code}
          organ={organ}
          onClick={() => onOpenOrgan(organ.organ_code)}
        />
      ))}
      {footer && <div className="pt-1">{footer}</div>}
    </div>
  );
}
